import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";
import { SERVICES } from "../../data/staticData";
import { Search as SearchIcon, ArrowUpRight } from "lucide-react";

const STATUS_FILTERS = ["all", "pending", "in_review", "in_progress", "delivered", "cancelled"];

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800",
  in_review: "bg-blue-100 text-blue-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

function serviceTitle(slug) {
  return SERVICES.find((s) => s.slug === slug)?.title || slug;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [service, setService] = useState("all");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status_filter = status;
    if (service !== "all") params.service = service;
    const [byFilter, all] = await Promise.all([
      api.get("/admin/orders", { params }),
      status === "all" && service === "all" ? Promise.resolve({ data: null }) : api.get("/admin/orders"),
    ]);
    setOrders(byFilter.data);
    setAllOrders(all.data ?? byFilter.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [status, service]);

  const visible = useMemo(() => {
    if (!q) return orders;
    const t = q.toLowerCase();
    return orders.filter((o) => `${o.order_id} ${o.customer_email} ${o.customer_name} ${o.service_slug} ${o.package_name || ""}`.toLowerCase().includes(t));
  }, [orders, q]);

  const counts = useMemo(() => {
    const base = { all: allOrders.length, pending: 0, in_review: 0, in_progress: 0, delivered: 0, cancelled: 0 };
    allOrders.forEach((o) => { base[o.status] = (base[o.status] || 0) + 1; });
    return base;
  }, [allOrders]);

  const TABS = [
    { v: "all", l: "All orders" },
    { v: "pending", l: "New" },
    { v: "in_review", l: "In review" },
    { v: "in_progress", l: "Ongoing" },
    { v: "delivered", l: "Completed" },
    { v: "cancelled", l: "Cancelled" },
  ];

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Orders queue</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">All incoming orders</h1>
          <p className="text-inkSoft mt-1">Every customer brief, in one queue.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_220px_220px] gap-3" data-testid="admin-orders-filters">
        <label className="relative">
          <SearchIcon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-inkSoft" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by order id, email, customer, service…"
            className="w-full h-11 pl-10 pr-4 rounded-full bg-white border border-line text-sm outline-none focus:border-ink"
            data-testid="admin-orders-search"
          />
        </label>
        <select value={service} onChange={(e) => setService(e.target.value)} className="h-11 rounded-full border border-line bg-white px-4 text-sm outline-none focus:border-ink">
          <option value="all">All services</option>
          {SERVICES.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 rounded-full border border-line bg-white px-4 text-sm outline-none focus:border-ink">
          {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace("_", " ")}</option>)}
        </select>
      </div>

      {/* Status tabs with live counts */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-order-tabs">
        {TABS.map((t) => {
          const active = status === t.v;
          const n = counts[t.v] ?? 0;
          return (
            <button
              key={t.v}
              onClick={() => setStatus(t.v)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${active ? "bg-ink text-white" : "bg-white border border-line text-inkSoft hover:text-ink"}`}
              data-testid={`admin-tab-${t.v}`}
            >
              {t.l}
              <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-mono ${active ? "bg-white/20 text-white" : "bg-surfaceAlt text-ink"}`}>{n}</span>
            </button>
          );
        })}
      </div>


      <div className="mt-6 rounded-2xl border border-line bg-white overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-sm text-inkSoft">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="px-6 py-10 text-sm text-inkSoft text-center">No orders match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-inkSoft border-b border-line bg-surfaceAlt/40">
                  <th className="px-6 py-4 font-medium">Order</th>
                  <th className="px-6 py-4 font-medium">Customer</th>
                  <th className="px-6 py-4 font-medium">Service</th>
                  <th className="px-6 py-4 font-medium">Created</th>
                  <th className="px-6 py-4 font-medium">Price</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((o) => (
                  <tr key={o.order_id} className="border-b border-line last:border-0 hover:bg-surfaceAlt/30" data-testid={`admin-order-${o.order_id}`}>
                    <td className="px-6 py-4 font-mono text-xs">{o.order_id}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{o.customer_name || "—"}</div>
                      <div className="text-xs text-inkSoft">{o.customer_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{serviceTitle(o.service_slug)}</div>
                      <div className="text-xs text-inkSoft">{o.package_name}</div>
                    </td>
                    <td className="px-6 py-4 text-inkSoft">{formatDate(o.created_at)}</td>
                    <td className="px-6 py-4 font-mono">${o.price ?? "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${STATUS_CLS[o.status] || "bg-gray-100 text-gray-800"}`}>{o.status.replace("_", " ")}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/admin/orders/${o.order_id}`} className="inline-flex items-center gap-1 text-terra font-medium text-sm">Open <ArrowUpRight size={14} /></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
